const CSRF_COOKIE_NAME = 'csrf-token';
const CSRF_HEADER_NAME = 'x-csrf-token';

const MAX_ATTEMPTS = 3;
const RETRY_DELAY = 1000;

async function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function fetchWithRetry(url: string, options: RequestInit, attempts: number = 1): Promise<Response> {
  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response;
  } catch (error) {
    if (attempts >= MAX_ATTEMPTS) {
      throw error;
    }
    await delay(RETRY_DELAY);
    return fetchWithRetry(url, options, attempts + 1);
  }
}

export async function setupCSRFToken(): Promise<void> {
  try {
    // First try to get the token from the cookie
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(cookie => cookie.trim().startsWith(`${CSRF_COOKIE_NAME}=`));
    
    if (csrfCookie) {
      // If we have a cookie, verify it's still valid
      const token = csrfCookie.split('=')[1];
      if (token) {
        return;
      }
    }

    // If no valid token found, request a new one
    const response = await fetchWithRetry('/api/csrf', {
      method: 'GET',
      credentials: 'include',
    });

    const data = await response.json();
    
    if (!data.token) {
      throw new Error('No CSRF token in response');
    }

    // Verify the token was set in the cookie
    const newCookies = document.cookie.split(';');
    const newCsrfCookie = newCookies.find(cookie => cookie.trim().startsWith(`${CSRF_COOKIE_NAME}=`));
    
    if (!newCsrfCookie) {
      throw new Error('CSRF token was not set in cookie');
    }

  } catch (error) {
    console.error('Failed to setup CSRF token:', error);
    throw error;
  }
}

export function getCSRFToken(): string | null {
  const cookies = document.cookie.split(';');
  const csrfCookie = cookies.find(cookie => cookie.trim().startsWith(`${CSRF_COOKIE_NAME}=`));
  return csrfCookie ? csrfCookie.split('=')[1] : null;
}

export async function fetchWithCSRF(url: string, options: RequestInit = {}): Promise<Response> {
  // Try to get existing token
  let token = getCSRFToken();
  
  // If no token, set it up
  if (!token) {
    await setupCSRFToken();
    token = getCSRFToken();
    if (!token) {
      throw new Error('Failed to obtain CSRF token');
    }
  }

  // Add CSRF token to headers
  const headers = new Headers(options.headers);
  headers.set(CSRF_HEADER_NAME, token);
  
  // Make the request
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers
  });
}
